"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Auth = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _url = require("@salesforce/pwa-kit-react-sdk/utils/url");
var _errors = require("@salesforce/pwa-kit-react-sdk/ssr/universal/errors");
var _pkce = require("./pkce");
var _utils = require("./utils");
var _constants = require("./constants");
var _jsCookie = _interopRequireDefault(require("js-cookie"));
var _jwtDecode = _interopRequireDefault(require("jwt-decode"));
var _utils2 = require("../utils/utils");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; } /*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */ /* eslint-disable no-unused-vars */ // @ts-ignore
// @ts-ignore
// @ts-ignore
/**
 * An object containing the customer's login credentials.
 * @typedef {Object} CustomerCredentials
 * @property {string} credentials.email
 * @property {string} credentials.password
 */

/**
 * Salesforce Customer object.
 * {@link https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customer}}
 * @typedef {Object} Customer
 */

/**
 * Enum for user types
 * @enum {string}
 */
const USER_TYPE = {
  REGISTERED: 'registered',
  GUEST: 'guest'
};
/**
 * A  class that provides auth functionality for the retail react app.
 */
const slasCallbackEndpoint = '/callback';
class Auth {
  constructor(api) {
    // @ts-ignore
    this._api = api;
    // @ts-ignore
    this._config = api._config;
    // @ts-ignore
    this._onClient = typeof window !== 'undefined';
    // @ts-ignore
    this._storageCopy = this._onClient ? new LocalStorage() : new Map();

    // To store tokens as cookies
    // change the next line to
    // this._storage = this._onClient ? new CookieStorage() : new Map()
    // @ts-ignore
    this._storage = this._onClient ? new LocalStorage() : new Map();
    const configOid = api._config.parameters.organizationId;
    if (!this.oid) {
      this.oid = configOid;
    }
    if (this.oid !== configOid) {
      this._clearAuth();
      this.oid = configOid;
    }
    if (api._config.initialAuthToken) {
      // @ts-ignore
      this.initialAuthToken = api._config.initialAuthToken;
    }
    if (api._config.initialRefreshToken) {
      // @ts-ignore
      this.initialRefreshToken = api._config.initialRefreshToken;
    }
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
  }

  /**
   * Returns the api client configuration
   * @returns {boolean}
   */
  get pendingLogin() {
    // @ts-ignore
    return this._pendingLogin;
  }
  get authToken() {
    // @ts-ignore
    return this._storage.get(_constants.tokenStorageKey);
  }
  get resetToken() {
    // @ts-ignore
    return this._storage.delete(_constants.tokenStorageKey);
  }
  set authToken(token) {
    // @ts-ignore
    this._storage.set(_constants.tokenStorageKey, token);
  }
  get userType() {
    // @ts-ignore
    return this._storage.get(_constants.refreshTokenRegisteredStorageKey) ? USER_TYPE.REGISTERED : USER_TYPE.GUEST;
  }
  get refreshToken() {
    const storageKey = this.userType === USER_TYPE.REGISTERED ? _constants.refreshTokenRegisteredStorageKey : _constants.refreshTokenGuestStorageKey;
    // @ts-ignore
    return this._storage.get(storageKey);
  }
  get usid() {
    // @ts-ignore
    return this._storage.get(_constants.usidStorageKey);
  }
  set usid(usid) {
    // @ts-ignore
    this._storage.set(_constants.usidStorageKey, usid);
  }
  get cid() {
    // @ts-ignore
    return this._storage.get(_constants.cidStorageKey);
  }
  set cid(cid) {
    // @ts-ignore
    this._storage.set(_constants.cidStorageKey, cid);
  }
  get encUserId() {
    // @ts-ignore
    return this._storage.get(_constants.encUserIdStorageKey);
  }
  set encUserId(encUserId) {
    // @ts-ignore
    this._storage.set(_constants.encUserIdStorageKey, encUserId);
  }
  get oid() {
    // @ts-ignore
    return this._storage.get(_constants.oidStorageKey);
  }
  set oid(oid) {
    // @ts-ignore
    this._storage.set(_constants.oidStorageKey, oid);
  }
  get isTokenValid() {
    return !(0, _utils.isTokenExpired)(this.authToken) &&
    // @ts-ignore
    !(0, _utils.hasSFRAAuthStateChanged)(this._storage, this._storageCopy);
  }
  _parseSlasJWT(jwt) {
    const payload = (0, _jwtDecode.default)(jwt);
    const {
      sub,
      isb,
      exp
    } = payload;
    if (!sub || !isb) {
      throw new Error('Unable to parse access token payload: missing sub and isb.');
    }

    // ISB format
    // 'uido:ecom::upn:Guest||xxxEmailxxx::uidn:FirstName LastName::gcid:xxxGuestCustomerIdxxx::rcid:xxxRegisteredCustomerIdxxx::chid:xxxSiteIdxxx',
    const isbParts = isb.split('::');
    const isGuest = isbParts[1] === 'upn:Guest';
    const customerId = isGuest ? isbParts[3].replace('gcid:', '') : isbParts[4].replace('rcid:', '');
    // SUB format
    // cc-slas::zzrf_001::scid:c9c45bfd-0ed3-4aa2-xxxx-40f88962b836::usid:b4865233-de92-4039-xxxx-aa2dfc8c1ea5
    const usid = sub.split('::')[3].replace('usid:', '');
    return {
      isGuest,
      customerId,
      usid,
      exp
    };
  }

  /**
   * Save refresh token in designated storage.
   *
   * @param {string} token The refresh token.
   * @param {USER_TYPE} type Type of the user.
   */
  _saveRefreshToken(token, type) {
    /**
     * For hybrid deployments, We store a copy of the refresh_token
     * to update access_token whenever customer auth state changes on SFRA.
     */
    if (type === USER_TYPE.REGISTERED) {
      // @ts-ignore
      this._storage.set(_constants.refreshTokenRegisteredStorageKey, token, {
        expires: _constants.REFRESH_TOKEN_COOKIE_AGE
      });
      // @ts-ignore
      this._storage.delete(_constants.refreshTokenGuestStorageKey);

      // @ts-ignore
      this._storageCopy.set(_constants.refreshTokenRegisteredStorageKey, token);
      // @ts-ignore
      this._storageCopy.delete(_constants.refreshTokenGuestStorageKey);
      return;
    }

    // @ts-ignore
    this._storage.set(_constants.refreshTokenGuestStorageKey, token, {
      expires: _constants.REFRESH_TOKEN_COOKIE_AGE
    });
    // @ts-ignore
    this._storage.delete(_constants.refreshTokenRegisteredStorageKey);

    // @ts-ignore
    this._storageCopy.set(_constants.refreshTokenGuestStorageKey, token);
    // @ts-ignore
    this._storageCopy.delete(_constants.refreshTokenRegisteredStorageKey);
  }

  /**
   * Called with the details from the redirect page that _loginWithCredentials returns
   * I think it's best we leave it to developers on how and where to call from
   * @param {{grantType, code, usid, codeVerifier, redirectUri}} requestDetails - The cutomerId of customer to get.
   */
  getLoggedInToken(requestDetails) {
    var _this = this;
    return (0, _asyncToGenerator2.default)(function* () {
      const data = new URLSearchParams();
      const {
        grantType,
        code,
        usid,
        codeVerifier,
        redirectUri
      } = requestDetails;
      data.append('code', code);
      data.append('grant_type', grantType);
      data.append('usid', usid);
      data.append('code_verifier', codeVerifier);
      // @ts-ignore
      data.append('client_id', _this._config.parameters.clientId);
      data.append('redirect_uri', redirectUri);
      // @ts-ignore
      data.append('channel_id', _this._config.parameters.siteId);
      const options = {
        headers: {
          'Content-Type': `application/x-www-form-urlencoded`
        },
        body: data
      };

      // @ts-ignore
      const response = yield _this._api.shopperLogin.getAccessToken(options);
      // Check for error response before handling the token
      if (response.status_code) {
        throw new _errors.HTTPError(response.status_code, response.message);
      }
      _this._handleShopperLoginTokenResponse(response);
      return response;
    })();
  }

  /**
   * Make a post request to the OCAPI /session endpoint to bridge the session.
   *
   * The HTTP response contains a set-cookie header which sets the dwsid session cookie.
   * This cookie is used on SFRA, and it allows shoppers to navigate between SFRA and
   * this PWA site seamlessly; this is often used to enable hybrid deployment.
   *
   * (Note: this method is client side only, b/c MRT doesn't support set-cookie header right now)
   *
   * @returns {Promise}
   */
  createOCAPISession() {
    return fetch(`${(0, _url.getAppOrigin)()}/mobify/proxy/ocapi/s/${
    // @ts-ignore
    this._config.parameters.siteId}/dw/shop/v22_8/sessions`, {
      method: 'POST',
      headers: {
        Authorization: this.authToken
      }
    });
  }
  _loginWithInitialAuthToken() {
    var _this2 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      const {
        isGuest,
        customerId,
        usid
      } = _this2._parseSlasJWT(
      // @ts-ignore
      _this2._config.initialAuthToken);

      // @ts-ignore
      if (_this2._config.initialAuthToken && _this2._config.initialRefreshToken) {
        // @ts-ignore
        _this2.authToken = `Bearer ${_this2._config.initialAuthToken}`;
        _this2.usid = usid;
        _this2.cid = customerId;

        // @ts-ignore
        _this2._saveRefreshToken(
        // @ts-ignore
        _this2._config.initialRefreshToken, isGuest ? USER_TYPE.GUEST : USER_TYPE.REGISTERED);
        (0, _utils.checkAuthTokenUsage)('appAuth',
        // @ts-ignore
        _this2._config.initialAuthToken);
        if (_this2.isTokenValid) {
          const customer = {
            customerId,
            authType: isGuest ? USER_TYPE.GUEST : USER_TYPE.REGISTERED
          };
          return customer;
        } else if (_this2.refreshToken && _this2.refreshToken !== 'no-token') {
          return yield _this2._refreshAccessToken();
        }
      }
      return yield _this2._loginAsGuest();
    })();
  }

  /**
   * Authorizes the customer as a registered or guest user.
   * @param {CustomerCredentials} [credentials]
   * @returns {Promise}
   */
  login(credentials) {
    var _this3 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      // Calling login while its already pending will return a reference
      // to the existing promise.
      // @ts-ignore
      if (_this3._pendingLogin) {
        // @ts-ignore
        return _this3._pendingLogin;
      }
      let retries = 0;
      const startLoginFlow = () => {
        let authorizationMethod = '_loginAsGuest';
        if (credentials) {
          authorizationMethod = '_loginWithCredentials';
        } else if (_this3.isTokenValid) {
          authorizationMethod = '_reuseCurrentLogin';
        } else if (_this3.refreshToken) {
          authorizationMethod = '_refreshAccessToken';
          // @ts-ignore
        } else if (!_utils2.isServer &&
        // @ts-ignore
        _this3._config.initialAuthToken) {
          authorizationMethod = '_loginWithInitialAuthToken';
        }
        return (
          // @ts-ignore
          _this3[authorizationMethod](credentials)
          // @ts-ignore
          .then(result => {
            // Uncomment the following line for phased launch
            // @ts-ignore
            _this3._onClient && _this3.createOCAPISession();
            return result;
          })
          // @ts-ignore
          .catch(error => {
            const retryErrors = [_constants.INVALID_TOKEN, _constants.EXPIRED_TOKEN];
            if (retries === 0 && retryErrors.includes(error.message)) {
              retries = 1; // we only retry once
              _this3._clearAuth();
              return startLoginFlow();
            }
            throw error;
          })
        );
      };

      // @ts-ignore
      _this3._pendingLogin = startLoginFlow().finally(() => {
        // When the promise is resolved, we need to remove the reference so
        // that subsequent calls to `login` can proceed.
        // @ts-ignore
        _this3._pendingLogin = undefined;
      });

      // @ts-ignore
      return _this3._pendingLogin;
    })();
  }

  /**
   * Clears the stored auth token and optionally logs back in as guest.
   * @param {boolean} [shouldLoginAsGuest=true] - Indicates if we should automatically log back in as a guest
   * @returns {(Promise<Customer>|undefined)}
   */
  logout(shouldLoginAsGuest = true) {
    var _this4 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      const options = {
        parameters: {
          refresh_token: _this4.refreshToken,
          // @ts-ignore
          client_id: _this4._config.parameters.clientId,
          // @ts-ignore
          channel_id: _this4._config.parameters.siteId
        }
      };
      // @ts-ignore
      yield _this4._api.shopperLogin.logoutCustomer(options, true);
      yield _this4._clearAuth();
      if (shouldLoginAsGuest) {
        // @ts-ignore
        return _this4.login();
      }
    })();
  }

  /**
   * Handles Response from ShopperLogin GetAccessToken, calls the getCustomer method and removes the PCKE code verifier from session storage
   * @private
   * @param {object} tokenResponse - access_token,id_token,refresh_token, expires_in,token_type, usid, customer_id, enc_user_id, idp_access_token
   */
  _handleShopperLoginTokenResponse(tokenResponse) {
    const {
      access_token,
      refresh_token,
      customer_id,
      usid,
      enc_user_id,
      id_token
    } = tokenResponse;
    this.authToken = `Bearer ${access_token}`;
    this.usid = usid;
    this.cid = customer_id;

    // we use id_token to distinguish guest and registered users
    if (id_token.length > 0) {
      this.encUserId = enc_user_id;
      this._saveRefreshToken(refresh_token, USER_TYPE.REGISTERED);
    } else {
      this._saveRefreshToken(refresh_token, USER_TYPE.GUEST);
    }

    // @ts-ignore
    if (this._onClient) {
      sessionStorage.removeItem('codeVerifier');
    }
  }
  _reuseCurrentLogin() {
    var _this5 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      // we're reusing the same token so we just need to return the customer object already associated with the token
      const customer = {
        authType: _this5.userType,
        customerId: _this5.cid
      };
      return customer;
    })();
  }

  /**
   * Begins oAuth PCKE Flow
   * @param {{email, password}}} credentials - User Credentials.
   * @returns {object} - a skeleton registered customer object that can be used to retrieve a complete customer object
   */
  _loginWithCredentials(credentials) {
    var _this6 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      const codeVerifier = (0, _pkce.createCodeVerifier)();
      const codeChallenge = yield (0, _pkce.generateCodeChallenge)(codeVerifier);
      sessionStorage.setItem('codeVerifier', codeVerifier);
      const authorization = `Basic ${btoa(`${credentials.email}:${credentials.password}`)}`;
      const options = {
        headers: {
          Authorization: authorization,
          'Content-Type': `application/x-www-form-urlencoded`
        },
        body: {
          redirect_uri: `${(0, _url.getAppOrigin)()}${slasCallbackEndpoint}`,
          // @ts-ignore
          client_id: _this6._config.parameters.clientId,
          code_challenge: codeChallenge,
          // @ts-ignore
          channel_id: _this6._config.parameters.siteId,
          usid: _this6.usid // mergeBasket API requires guest usid to be sent in the authToken
        }
      };

      // @ts-ignore
      const response = yield _this6._api.shopperLogin.authenticateCustomer(options, true);
      if (response.status >= 400) {
        const json = yield response.json();
        throw new _errors.HTTPError(response.status, json.message);
      }
      const tokenBody = (0, _utils.createGetTokenBody)(response.url, `${(0, _url.getAppOrigin)()}${slasCallbackEndpoint}`,
      // @ts-ignore
      window.sessionStorage.getItem('codeVerifier'));
      const token = yield _this6.getLoggedInToken(tokenBody);
      const passedSecurityCheck = (0, _utils.checkAuthTokenUsage)('credAuth', token.access_token, credentials.email);
      if (!passedSecurityCheck) {
        throw new _errors.HTTPError(400, 'SLAS Error: Token mismatch');
      }
      const {
        customer_id
      } = token;
      const customer = {
        customerId: customer_id,
        authType: USER_TYPE.REGISTERED
      };
      return customer;
    })();
  }

  /**
   * Begins oAuth PCKE Flow for guest
   * @returns {object} - a guest customer object
   */
  _loginAsGuest() {
    var _this7 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      const codeVerifier = (0, _pkce.createCodeVerifier)();
      const codeChallenge = yield (0, _pkce.generateCodeChallenge)(codeVerifier);

      // @ts-ignore
      if (_this7._onClient) {
        sessionStorage.setItem('codeVerifier', codeVerifier);
      }
      const options = {
        headers: {
          Authorization: '',
          'Content-Type': `application/x-www-form-urlencoded`
        },
        parameters: {
          redirect_uri: `${(0, _url.getAppOrigin)()}${slasCallbackEndpoint}`,
          // @ts-ignore
          client_id: _this7._config.parameters.clientId,
          code_challenge: codeChallenge,
          response_type: 'code',
          hint: 'guest'
        }
      };

      // @ts-ignore
      const response = yield _this7._api.shopperLogin.authorizeCustomer(options, true);
      if (response.status >= 400) {
        let text = yield response.text();
        let errorMessage = text;
        try {
          const data = JSON.parse(text);
          if (data.message) {
            errorMessage = data.message;
          }
        } catch {} // eslint-disable-line no-empty
        throw new _errors.HTTPError(response.status, errorMessage);
      }
      const tokenBody = (0, _utils.createGetTokenBody)(response.url, `${(0, _url.getAppOrigin)()}${slasCallbackEndpoint}`,
      // @ts-ignore
      _this7._onClient ? window.sessionStorage.getItem('codeVerifier') : codeVerifier);
      const {
        customer_id
      } = yield _this7.getLoggedInToken(tokenBody);

      // A guest customerId will never return a customer from the customer endpoint
      const customer = {
        authType: USER_TYPE.GUEST,
        customerId: customer_id
      };
      return customer;
    })();
  }

  /**
   * Creates a guest session
   * @private
   * @returns {*} - The response to be passed back to original caller.
   */
  _createGuestSession() {
    var _this8 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      const loginType = 'guest';
      const options = {
        body: {
          type: loginType
        }
      };

      // @ts-ignore
      const rawResponse = yield _this8._api.shopperCustomers.authorizeCustomer(options, true);
      return rawResponse;
    })();
  }

  /**
   * Refreshes Logged In Token
   * @private
   * @returns {<Promise>} - Handle Shopper Login Promise
   */
  _refreshAccessToken() {
    var _this9 = this;
    return (0, _asyncToGenerator2.default)(function* () {
      const data = new URLSearchParams();
      const currentAuthToken = _this9.authToken;
      data.append('grant_type', 'refresh_token');
      data.append('refresh_token', _this9.refreshToken);
      // @ts-ignore
      data.append('client_id', _this9._config.parameters.clientId);
      const options = {
        headers: {
          'Content-Type': `application/x-www-form-urlencoded`
        },
        body: data
      };
      // @ts-ignore
      const response = yield _this9._api.shopperLogin.getAccessToken(options);

      // Check for error response before handling the token
      if (response.message && response.message === _constants.INVALID_TOKEN) {
        throw new _errors.HTTPError(400, response.message);
      }
      _this9._handleShopperLoginTokenResponse(response);
      if (currentAuthToken) {
        var _currentAccessToken$i, _currentAccessToken$i2;
        const currentAccessToken = (0, _jwtDecode.default)(currentAuthToken);
        const currentTokenEmail = currentAccessToken === null || currentAccessToken === void 0 ? void 0 : (_currentAccessToken$i = currentAccessToken.isb) === null || _currentAccessToken$i === void 0 ? void 0 : (_currentAccessToken$i2 = _currentAccessToken$i.match(/upn:([^:]+)/)) === null || _currentAccessToken$i2 === void 0 ? void 0 : _currentAccessToken$i2[1];
        const passedSecurityCheck = (0, _utils.checkAuthTokenUsage)('refreshToken', response.access_token, currentTokenEmail);
        if (!passedSecurityCheck) {
          _this9.logout();
          throw new _errors.HTTPError(400, 'SLAS Error: Token mismatch');
        }
      }
      const {
        id_token,
        enc_user_id,
        customer_id
      } = response;
      let customer = {
        authType: USER_TYPE.GUEST,
        customerId: customer_id
      };
      // Determining if registered customer or guest
      if (id_token.length > 0 && enc_user_id.length > 0) {
        customer.authType = USER_TYPE.REGISTERED;
      }
      return customer;
    })();
  }

  /**
   * Removes the stored auth token.
   * @private
   */
  _clearAuth() {
    if (typeof window !== 'undefined') {
      window.sessionStorage.clear();
      // @ts-ignore
      this._storage.delete(_constants.tokenStorageKey);
      // @ts-ignore
      this._storage.delete(_constants.refreshTokenRegisteredStorageKey);
      // @ts-ignore
      this._storage.delete(_constants.refreshTokenGuestStorageKey);
      // @ts-ignore
      this._storage.delete(_constants.usidStorageKey);
      // @ts-ignore
      this._storage.delete(_constants.cidStorageKey);
      // @ts-ignore
      this._storage.delete(_constants.encUserIdStorageKey);
      // @ts-ignore
      this._storage.delete(_constants.dwSessionIdKey);
      // @ts-ignore
      this._initialAuthToken = undefined;
      // @ts-ignore
      this._inititalRefreshToken = undefined;
    }
  }
}
exports.Auth = Auth;
class Storage {
  set(key, value, options) {}
  get(key) {}
  delete(key) {}
}
class CookieStorage extends Storage {
  constructor(...args) {
    // @ts-ignore
    super(args);
    if (typeof document === 'undefined') {
      throw new Error('CookieStorage is not avaliable on the current environment.');
    }
  }
  set(key, value, options) {
    _jsCookie.default.set(key, value, _objectSpread({
      secure: true
    }, options));
  }
  get(key) {
    return _jsCookie.default.get(key);
  }
  delete(key) {
    _jsCookie.default.remove(key);
  }
}
class LocalStorage extends Storage {
  constructor(...args) {
    // @ts-ignore
    super(args);
    if (typeof window === 'undefined') {
      throw new Error('LocalStorage is not avaliable on the current environment.');
    }
  }
  set(key, value) {
    window.localStorage.setItem(key, value);
  }
  get(key) {
    return window.localStorage.getItem(key);
  }
  delete(key) {
    window.localStorage.removeItem(key);
  }
}